import type { IKeyObjective } from 'legacy/lut/classes/models/partner360/IFormOptionsJBP'

export interface ICockpitBuStrategicInitiative {
  root: string
  leaf: string
  breadcrumb: string
  rootOrder: number
  leafOrder: number
}

const getOfferings = (value?: string): string[] =>
  (value ?? '')
    .split(/[;,]/)
    .map((offering) => offering.trim())
    .filter(Boolean)

/**
 * Creates an offering-aware resolver for the KeyObjectives tree stored in JBP Settings.
 * Offering-specific nodes override global nodes with the same key, which matters when different
 * offerings reuse a Development label under different roots.
 */
export const createCockpitBuStrategicInitiativeResolver = (keyObjectives: IKeyObjective[]) => {
  const objectivesByOffering = new Map<
    string,
    Map<string, { objective: IKeyObjective; order: number }>
  >()

  const getObjectivesByKey = (programType: string | null) => {
    const normalizedProgramType = programType?.trim() ?? ''
    const cached = objectivesByOffering.get(normalizedProgramType)

    if (cached) {
      return cached
    }

    const objectivesByKey = new Map<string, { objective: IKeyObjective; order: number }>()

    keyObjectives.forEach((objective, order) => {
      const key = objective.key.trim()
      const offerings = getOfferings(objective.offerings)
      const isGlobal = offerings.length === 0

      if (!key || (!isGlobal && !offerings.includes(normalizedProgramType))) {
        return
      }

      if (isGlobal) {
        if (!objectivesByKey.has(key)) {
          objectivesByKey.set(key, { objective, order })
        }
      } else {
        objectivesByKey.set(key, { objective, order })
      }
    })

    objectivesByOffering.set(normalizedProgramType, objectivesByKey)

    return objectivesByKey
  }

  return (development: string, programType: string | null): ICockpitBuStrategicInitiative => {
    const fallback = development.trim()
    const objectivesByKey = getObjectivesByKey(programType)
    const breadcrumbParts: string[] = []
    const breadcrumbOrders: number[] = []
    const visitedKeys = new Set<string>()
    let currentKey = fallback

    while (currentKey && !visitedKeys.has(currentKey)) {
      const objectiveWithOrder = objectivesByKey.get(currentKey)

      if (!objectiveWithOrder) {
        break
      }

      visitedKeys.add(currentKey)
      const { objective, order } = objectiveWithOrder
      breadcrumbParts.unshift(objective.key.trim())
      breadcrumbOrders.unshift(order)
      currentKey = objective.parent?.trim() ?? ''
    }

    if (!breadcrumbParts.length) {
      return {
        root: fallback,
        leaf: fallback,
        breadcrumb: fallback,
        rootOrder: Number.MAX_SAFE_INTEGER,
        leafOrder: Number.MAX_SAFE_INTEGER,
      }
    }

    return {
      root: breadcrumbParts[0],
      leaf: breadcrumbParts[breadcrumbParts.length - 1],
      breadcrumb: breadcrumbParts.join(' > '),
      rootOrder: breadcrumbOrders[0],
      leafOrder: breadcrumbOrders[breadcrumbOrders.length - 1],
    }
  }
}
